import React, { memo, useCallback, useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  H4,
  H5,
  TextInput,
  Button,
  Small,
  Toggle,
} from "orcs-design-system";
import { useWorkflowBuilder } from "src/contexts/workflowBuilderContext";
import ConfigForm from "../ConfigForm";

const PanelContainer = styled.div`
  width: ${(props) => (props.$isOpen ? "360px" : "0")};
  height: 100%;
  background: ${themeGet("colors.white")};
  border-left: 1px solid ${themeGet("colors.greyLighter")};
  overflow: hidden;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  padding: ${themeGet("space.r")};
  border-bottom: 1px solid ${themeGet("colors.greyLighter")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${themeGet("colors.grey")};
  border-radius: 4px;

  &:hover {
    background: ${themeGet("colors.greyLightest")};
    color: ${themeGet("colors.greyDarker")};
  }
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${themeGet("space.r")};
`;

const Section = styled.div`
  margin-bottom: ${themeGet("space.r")};
`;

const SectionTitle = styled(H5)`
  margin-bottom: ${themeGet("space.s")};
  color: ${themeGet("colors.greyDark")};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldGroup = styled.div`
  margin-bottom: ${themeGet("space.r")};
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${themeGet("colors.greyDark")};
  margin-bottom: 4px;
`;

const HelpText = styled.div`
  font-size: 11px;
  color: ${themeGet("colors.grey")};
  margin-top: 4px;
`;

const DependencyList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const DependencyTag = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${themeGet("colors.greyLightest")};
  border-radius: 4px;
  font-size: 12px;
  color: ${themeGet("colors.greyDark")};
`;

const DeleteSection = styled.div`
  border-top: 1px solid ${themeGet("colors.greyLighter")};
  padding-top: ${themeGet("space.r")};
  margin-top: ${themeGet("space.r")};
`;

const ModeToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: ${themeGet("space.s")};
  font-size: 11px;
  color: ${themeGet("colors.grey")};
`;

const JsonEditor = styled.textarea`
  width: 100%;
  min-height: 150px;
  font-family: monospace;
  font-size: 12px;
  padding: ${themeGet("space.s")};
  border: 1px solid ${themeGet("colors.greyLighter")};
  border-radius: ${themeGet("radii.1")};
  resize: vertical;
  background: ${themeGet("colors.greyLightest")};

  &:focus {
    outline: none;
    border-color: ${themeGet("colors.primary")};
  }
`;

const ComponentDescription = styled.div`
  font-size: 12px;
  color: ${themeGet("colors.grey")};
  padding: ${themeGet("space.s")};
  background: ${themeGet("colors.greyLightest")};
  border-radius: ${themeGet("radii.1")};
  margin-bottom: ${themeGet("space.r")};
  border-left: 3px solid ${themeGet("colors.primary")};
`;

function PropertiesPanel() {
  const {
    selectedNode,
    isPropertiesPanelOpen,
    togglePropertiesPanel,
    updateNode,
    deleteNode,
    nodes,
    componentTypes,
  } = useWorkflowBuilder();

  const [localName, setLocalName] = useState("");
  const [localConfig, setLocalConfig] = useState({});
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("{}");
  const [jsonError, setJsonError] = useState(null);

  // Get the schema for the selected component type
  const componentInfo = useMemo(() => {
    if (!selectedNode?.data?.componentType) {
      return null;
    }
    return componentTypes.find(
      (c) => c.type === selectedNode.data.componentType
    );
  }, [selectedNode, componentTypes]);

  // Compute available source fields from upstream dependencies
  // This looks at "to" fields from field-map transforms and common fields
  const availableSourceFields = useMemo(() => {
    if (!selectedNode) {
      return [];
    }

    const fields = new Set();
    const dependencies =
      selectedNode.data.dependencies || selectedNode.data.dependsOn || [];

    // Look at each dependency and extract potential output fields
    dependencies.forEach((depKey) => {
      const depNode = nodes.find((n) => n.data.stepKey === depKey);
      if (!depNode) {
        return;
      }

      const depConfig = depNode.data.config || {};

      // For field-map steps, extract "to" fields as available fields
      if (
        depNode.data.componentType === "field-map" &&
        Array.isArray(depConfig.mappings)
      ) {
        depConfig.mappings.forEach((mapping) => {
          if (mapping.to) {
            fields.add(mapping.to);
          }
        });
        // If includeUnmapped is true, also include "from" fields
        if (depConfig.includeUnmapped && Array.isArray(depConfig.mappings)) {
          depConfig.mappings.forEach((mapping) => {
            if (mapping.from) {
              fields.add(mapping.from);
            }
          });
        }
      }
    });

    return Array.from(fields).sort();
  }, [selectedNode, nodes]);

  // Sync local state with selected node
  useEffect(() => {
    if (selectedNode) {
      setLocalName(selectedNode.data.name || "");
      setLocalConfig(selectedNode.data.config || {});
      setJsonText(JSON.stringify(selectedNode.data.config || {}, null, 2));
      setJsonError(null);
    }
  }, [selectedNode]);

  const handleNameChange = useCallback(
    (e) => {
      const newName = e.target.value;
      setLocalName(newName);
      if (selectedNode) {
        updateNode(selectedNode.id, { name: newName });
      }
    },
    [selectedNode, updateNode]
  );

  // Handle config changes from the form
  const handleConfigChange = useCallback(
    (newConfig) => {
      setLocalConfig(newConfig);
      setJsonText(JSON.stringify(newConfig, null, 2));
      if (selectedNode) {
        updateNode(selectedNode.id, { config: newConfig });
      }
    },
    [selectedNode, updateNode]
  );

  // Handle JSON text changes
  const handleJsonTextChange = useCallback((e) => {
    setJsonText(e.target.value);
    setJsonError(null);
  }, []);

  // Apply JSON changes
  const handleJsonBlur = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText);
      setLocalConfig(parsed);
      setJsonError(null);
      if (selectedNode) {
        updateNode(selectedNode.id, { config: parsed });
      }
    } catch {
      setJsonError("Invalid JSON format");
    }
  }, [jsonText, selectedNode, updateNode]);

  // Toggle between form and JSON mode
  const handleModeToggle = useCallback(() => {
    if (!jsonMode) {
      // Switching to JSON mode - sync text
      setJsonText(JSON.stringify(localConfig, null, 2));
    }
    setJsonMode(!jsonMode);
    setJsonError(null);
  }, [jsonMode, localConfig]);

  const handleDelete = useCallback(() => {
    if (!selectedNode) {
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this step?"
    );
    if (confirmed) {
      deleteNode(selectedNode.id);
    }
  }, [selectedNode, deleteNode]);

  if (!selectedNode) {
    return (
      <PanelContainer $isOpen={isPropertiesPanelOpen}>
        <PanelContent>
          <Box textAlign="center" py="xl">
            <FontAwesomeIcon
              icon={["fas", "mouse-pointer"]}
              style={{ fontSize: 32, opacity: 0.3, marginBottom: 16 }}
            />
            <Small color="grey" display="block">
              Select a step to view properties
            </Small>
          </Box>
        </PanelContent>
      </PanelContainer>
    );
  }

  // Get dependencies - support both naming conventions
  const dependencies =
    selectedNode.data.dependencies || selectedNode.data.dependsOn || [];

  return (
    <PanelContainer $isOpen={isPropertiesPanelOpen}>
      <PanelHeader>
        <H4 mb="0">Step Properties</H4>
        <CloseButton onClick={togglePropertiesPanel}>
          <FontAwesomeIcon icon={["fas", "times"]} />
        </CloseButton>
      </PanelHeader>

      <PanelContent>
        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={["fas", "info-circle"]} />
            Basic Info
          </SectionTitle>

          {componentInfo?.description && (
            <ComponentDescription>
              <FontAwesomeIcon
                icon={["fas", "lightbulb"]}
                style={{ marginRight: 6 }}
              />
              {componentInfo.description}
            </ComponentDescription>
          )}

          <FieldGroup>
            <Label htmlFor="step-name">Name</Label>
            <TextInput
              id="step-name"
              value={localName}
              onChange={handleNameChange}
              placeholder="Step name"
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Step Key</Label>
            <TextInput
              id="step-key"
              value={selectedNode.data.stepKey || ""}
              disabled
              style={{ opacity: 0.7 }}
            />
            <HelpText>Auto-generated unique identifier</HelpText>
          </FieldGroup>

          <FieldGroup>
            <Label>Component Type</Label>
            <TextInput
              id="component-type"
              value={
                componentInfo?.name || selectedNode.data.componentType || ""
              }
              disabled
              style={{ opacity: 0.7 }}
            />
          </FieldGroup>
        </Section>

        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={["fas", "code-branch"]} />
            Dependencies
          </SectionTitle>

          <HelpText style={{ marginBottom: 8 }}>
            Connect this step to others by drawing edges on the canvas
          </HelpText>

          {dependencies.length > 0 ? (
            <DependencyList>
              {dependencies.map((depKey) => {
                const depNode = nodes.find((n) => n.data.stepKey === depKey);
                return (
                  <DependencyTag key={depKey}>
                    <FontAwesomeIcon
                      icon={["fas", "link"]}
                      style={{ fontSize: 10 }}
                    />
                    {depNode?.data.name || depKey}
                  </DependencyTag>
                );
              })}
            </DependencyList>
          ) : (
            <Small color="grey">No dependencies (entry step)</Small>
          )}
        </Section>

        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={["fas", "cog"]} />
            Configuration
          </SectionTitle>

          <ModeToggle>
            <Small>Form</Small>
            <Toggle
              id="config-mode-toggle"
              checked={jsonMode}
              onChange={handleModeToggle}
              small
            />
            <Small>JSON</Small>
          </ModeToggle>

          {jsonMode ? (
            <>
              <JsonEditor
                value={jsonText}
                onChange={handleJsonTextChange}
                onBlur={handleJsonBlur}
                placeholder='{"key": "value"}'
                spellCheck={false}
              />
              {jsonError && (
                <Small color="danger" display="block" mt="s">
                  <FontAwesomeIcon icon={["fas", "exclamation-circle"]} />{" "}
                  {jsonError}
                </Small>
              )}
            </>
          ) : (
            <ConfigForm
              schema={componentInfo?.configSchema}
              config={localConfig}
              onChange={handleConfigChange}
              availableSourceFields={availableSourceFields}
            />
          )}
        </Section>

        <DeleteSection>
          <Button
            variant="danger"
            fullWidth
            onClick={handleDelete}
            iconLeft={["fas", "trash"]}
          >
            Delete Step
          </Button>
        </DeleteSection>
      </PanelContent>
    </PanelContainer>
  );
}

export default memo(PropertiesPanel);
